'use client'

import type { ReactElement } from 'react'
import React, { createContext, useMemo } from 'react'

import type { MarketType } from 'src/types/market'

import { addDays } from 'date-fns'
import { getMinBookingDate } from 'src/context/search/utils'

const MarketContext = createContext<(MarketType & { minBookingDate: Date }) | null>(null)

const MarketProvider = ({
  children,
  value,
}: {
  children: ReactElement
  value: MarketType | null
}) => {
  if (value === null) {
    throw new Error('MarketContext provider can not have a null value')
  }

  const {
    countryCode,
    countryLanguage,
    countryName,
    contactEmail,
    currency,
    currencySymbol,
    language,
    locale,
    market,
    phone,
    measurementSystem,
    subdomain,
    topdomain,
    helpCenterUrl,
    faqsUrl,
    calendar,
    chatbot,
    feedbackMessageType,
    autoNewsletterSubscribe,
    overrideCreditCardLogos,
    paymentModal,
    isAsync,
  } = value

  const currentDate = new Date()

  const minBookingDateObject = getMinBookingDate(
    {
      year: currentDate.getFullYear(),
      month: currentDate.getMonth(),
      date: currentDate.getDate(),
      hours: currentDate.getHours(),
      minutes: currentDate.getMinutes(),
    },
    {
      year: currentDate.getUTCFullYear(),
      month: currentDate.getUTCMonth(),
      date: currentDate.getUTCDate(),
      hours: currentDate.getUTCHours(),
      minutes: currentDate.getUTCMinutes(),
    }
  )

  const { year: minBookingYear, month: minBookingMonth, date: minBookingDay } = minBookingDateObject
  const { inanitionDays } = calendar

  const minBookingDate = useMemo(
    () => addDays(new Date(minBookingYear, minBookingMonth, minBookingDay), inanitionDays),
    [minBookingDay, minBookingMonth, minBookingYear, inanitionDays]
  )

  const marketValue = useMemo(
    () => ({
      countryCode,
      countryLanguage,
      countryName,
      contactEmail,
      currency,
      currencySymbol,
      language,
      locale,
      market,
      phone,
      measurementSystem,
      subdomain,
      topdomain,
      helpCenterUrl,
      faqsUrl,
      calendar,
      minBookingDate,
      chatbot,
      feedbackMessageType,
      autoNewsletterSubscribe,
      overrideCreditCardLogos,
      paymentModal,
      isAsync,
    }),
    [
      countryCode,
      countryLanguage,
      countryName,
      contactEmail,
      currency,
      language,
      locale,
      market,
      phone,
      measurementSystem,
      subdomain,
      topdomain,
      helpCenterUrl,
      faqsUrl,
      calendar,
      minBookingDate,
      chatbot,
      feedbackMessageType,
      autoNewsletterSubscribe,
      overrideCreditCardLogos,
      paymentModal,
      isAsync,
    ]
  )

  return <MarketContext.Provider value={marketValue}>{children}</MarketContext.Provider>
}

export { MarketContext, MarketProvider }
